import React, { useLayoutEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useSysTheme } from 'hooks/useUiState'
import { SystemTheme } from 'hooks/useUiState/types'
import { useAppSelector } from 'app/hooks'
import {
  selectHasLabels,
  selectHasError,
  selectCategoryIsTemplate,
  selectDashboardInfoCategory
} from 'features/dashboardInfo/selectors'
import { CATEGORY } from 'features/dashboardInfo/constants'
import { DashboardCategory } from 'features/dashboardInfo/types'
import { HeapEventPage } from 'utils/heapUtils'

import LabelList from './LabelList'
import DashboardList from './DashboardList'
import SortingButton from './SortingButton'
import CreateButton from './CreateButton'
import CategoryButton from './CategoryButton'
import FilterButton from './FilterButton'
import { ReportPill } from './FilterPills'
import SearchBar from './SearchBar'
import ErrorHandle from './ErrorHandle'

const SidePanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`
const TOP_CONTAINER_HEIGHT = 99
const TopContainer = styled.div`
  padding: 12px 24px 16px 24px;
`
const CategoryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  & > :first-child {
    flex: 1;
  }
`
const FilterPillsContainer = styled.div`
  margin-top: 16px;
  display: flex;
  gap: 8px;
`

const BottomContainer = styled.div<{ $offset?: number; $onlyDashboard: boolean }>`
  display: flex;
  justify-content: center;
  flex: 1;
  height: ${(props) => `calc(100% - ${props.$offset}px)`};
  width: 100%;

  & > * {
    height: 100%;
    width: ${({ $onlyDashboard }) => ($onlyDashboard ? '100%' : 'calc((100% - 1px) / 2)')};
  }
`
const Divider = styled.div<{ $theme: SystemTheme }>`
  width: 1px;
  flex: none;
  background-color: ${({ $theme }) => $theme.common.divider[100]};
`

const SidePanel = () => {
  const theme = useSysTheme()
  const { t } = useTranslation()

  const hasLabels = useAppSelector(selectHasLabels)
  const hasError = useAppSelector(selectHasError)
  const categoryIsTemplate = useAppSelector(selectCategoryIsTemplate)
  const category = useAppSelector(selectDashboardInfoCategory)

  const topContainerRef = useRef<HTMLDivElement>(null)
  const [offset, setOffset] = useState(TOP_CONTAINER_HEIGHT)
  const [displayFilter, setDisplayFilter] = useState(false)

  const hasCreateButton = !([
    CATEGORY.companyDashboards,
    CATEGORY.myTemplates,
    CATEGORY.companyTemplates
  ] as DashboardCategory[]).includes(category)

  const handleTopContainerHeightResize = () => {
    if (topContainerRef.current) {
      const { height } = topContainerRef.current.getBoundingClientRect()
      if (height !== offset) setOffset(height)
    }
  }

  useLayoutEffect(() => {
    handleTopContainerHeightResize()
  })

  return (
    <SidePanelContainer>
      <TopContainer ref={topContainerRef}>
        <CategoryRow>
          <CategoryButton style={{ marginLeft: '-8px' }} page={HeapEventPage.DashboardView} />
          {hasCreateButton && <CreateButton />}
        </CategoryRow>
        <FilterRow>
          <SearchBar
            placeholder={categoryIsTemplate ? t('searchTemplates') : t('searchDashboards')}
            page={HeapEventPage.DashboardView}
          />
          <FilterButton
            onClick={() => setDisplayFilter(!displayFilter)}
            displayFilter={displayFilter}
          />
          <SortingButton />
        </FilterRow>
        {displayFilter && (
          <FilterPillsContainer>
            <ReportPill page={HeapEventPage.DashboardView} />
          </FilterPillsContainer>
        )}
      </TopContainer>

      <BottomContainer $offset={offset} $onlyDashboard={!hasLabels}>
        {hasError ? (
          <ErrorHandle />
        ) : (
          <>
            {hasLabels && (
              <>
                <LabelList page={HeapEventPage.DashboardView} />
                <Divider $theme={theme} />
              </>
            )}
            <DashboardList />
          </>
        )}
      </BottomContainer>
    </SidePanelContainer>
  )
}

export default SidePanel
